import React from 'react';
import { Mountain } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useLanguage } from '@/contexts/LanguageContext';

const TENERIFE = { lat: 28.27, lon: -16.55 };

const T = {
  es: { note700: 'Mapa a unos 3.000 m (700 hPa). La tabla y las etiquetas de Windy son a nivel del modelo y pueden marcar más calor que en la cumbre.', note800: 'Mapa a unos 2.000 m (800 hPa). La tabla y las etiquetas de Windy son a nivel del modelo y pueden marcar más calor que en la cumbre.', mountain: 'Previsión de alta montaña' },
  en: { note700: "Map at about 3,000 m (700 hPa). Windy's table and labels use the model's ground level and may read warmer than the summit.", note800: "Map at about 2,000 m (800 hPa). Windy's table and labels use the model's ground level and may read warmer than the summit.", mountain: 'Mountain forecast' },
  de: { note700: 'Karte auf etwa 3.000 m (700 hPa). Tabelle und Beschriftungen von Windy beziehen sich auf die Modellhöhe und können wärmer sein als am Gipfel.', note800: 'Karte auf etwa 2.000 m (800 hPa). Tabelle und Beschriftungen von Windy beziehen sich auf die Modellhöhe und können wärmer sein als am Gipfel.', mountain: 'Bergwetter' },
};

export const windyLevel = (elevation) => {
  if (elevation == null) return 'surface';
  if (elevation >= 3000) return '700h';
  if (elevation >= 1500) return '800h';
  return 'surface';
};

export const windyUrl = (cam) => {
  const lat = cam?.latitude ?? TENERIFE.lat, lon = cam?.longitude ?? TENERIFE.lon;
  const level = windyLevel(cam?.elevation);
  const overlay = level === 'surface' ? 'wind' : 'temp';
  return `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&detailLat=${lat}&detailLon=${lon}&zoom=10&level=${level}&overlay=${overlay}&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&metricWind=km%2Fh&metricTemp=%C2%B0C&metricRain=mm`;
};

const MOUNTAIN_URL = { '700h': 'https://www.mountain-forecast.com/peaks/Pico-de-Teide/forecasts/3718', '800h': 'https://www.mountain-forecast.com/peaks/Pico-de-Teide/forecasts/2000' };

export const WindySheet = ({ open, onOpenChange, cam, title }) => {
  const { language } = useLanguage();
  const t = T[language] || T.es;
  const level = windyLevel(cam?.elevation);
  const src = windyUrl(cam);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80vh] p-0 rounded-t-2xl overflow-hidden flex flex-col" data-testid="windy-sheet" data-level={level}>
        <SheetHeader className="px-4 pt-4 pb-2 text-left">
          <SheetTitle className="text-base">{title}</SheetTitle>
          <SheetDescription className="sr-only">Windy</SheetDescription>
        </SheetHeader>
        {open && (
          <iframe src={src} width="100%" frameBorder="0" title="Windy" className="w-full flex-1 min-h-0" data-testid="windy-iframe" />
        )}
        {level !== 'surface' && (
          <div className="px-4 py-3 bg-white border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="windy-level-note">
            <p className="text-xs text-gray-500 flex-1">{level === '700h' ? t.note700 : t.note800}</p>
            <a href={MOUNTAIN_URL[level]} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-[#006D77] border border-[#38B2AC]/40 hover:bg-[#38B2AC]/10 transition-colors whitespace-nowrap"
              data-testid="windy-mountain-btn">
              <Mountain size={14} /> {t.mountain}
            </a>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default WindySheet;
